/**
 * Shared Cognara claim-scan panels — rendered identically on the contract
 * Overview tab and inside the Cognara AI Review tab so the claim opportunities
 * Cognara finds in the selected documents are never buried in one place only.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin } from 'lucide-react';
import { ExplainButton } from './CognaraComponentChat';

const tone = (s) => ({
  STRONG: { bg: '#DCFCE7', color: '#166534' },
  ARGUABLE: { bg: '#FEF3C7', color: '#92400E' },
  WEAK: { bg: '#E2E8F0', color: '#334155' },
  HIGH: { bg: '#FEE2E2', color: '#991B1B' },
  MEDIUM: { bg: '#FEF3C7', color: '#92400E' },
  LOW: { bg: '#DCFCE7', color: '#166534' },
  P0: { bg: '#FEE2E2', color: '#991B1B' },
  P1: { bg: '#FEF3C7', color: '#92400E' },
  P2: { bg: '#DCFCE7', color: '#166534' },
})[s] || { bg: '#E2E8F0', color: '#334155' };

export function ClaimOpportunitiesTable({ items = [], onShowSource }) {
  if (!items.length) return null;
  return (
    <div className="overflow-x-auto" data-testid="claim-opportunities-table">
      <table className="w-full text-xs">
        <thead className="text-muted-foreground uppercase text-[10px]">
          <tr>{['ID', 'Opportunity', 'Type', 'Basis', 'Evidence', 'Notice / time-bar', 'Quantum', 'Win %', 'Strength', 'Next step', ''].map(h => (
            <th key={h} className="text-left px-2 py-1">{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {items.map((c, i) => (
            <tr key={c.id || i} className="border-t border-border align-top" data-testid={`claim-opp-${c.id || i}`}>
              <td className="px-2 py-1.5 font-mono text-[10px]">{c.id || `CO${i + 1}`}</td>
              <td className="px-2 py-1.5 text-foreground">
                {c.title}
                {c.alreadyRaised && <Badge variant="outline" className="ml-1 text-[9px]">already raised</Badge>}
                {c.triggerEvent && <div className="text-muted-foreground text-[10px] mt-0.5">{c.triggerEvent}</div>}
              </td>
              <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{c.claimType}</Badge></td>
              <td className="px-2 py-1.5 text-foreground/80">
                {c.entitlementBasis}
                {c.clauseReference && <div className="text-muted-foreground text-[10px]">Cl. {c.clauseReference}</div>}
              </td>
              <td className="px-2 py-1.5 text-[10px]">
                {c.documentReference && <div className="text-blue-400 truncate max-w-[150px]" title={c.documentReference}>{c.documentReference}</div>}
                {c.quotedText && <div className="text-muted-foreground italic truncate max-w-[180px]" title={c.quotedText}>“{c.quotedText}”</div>}
              </td>
              <td className="px-2 py-1.5 text-[10px]">
                <div className="text-foreground/80">{c.noticeStatus || '—'}{c.noticeDeadlineDays ? ` · ${c.noticeDeadlineDays}` : ''}</div>
                {c.timeBarRisk && <span style={{ background: tone(String(c.timeBarRisk).split(' ')[0]).bg, color: tone(String(c.timeBarRisk).split(' ')[0]).color }} className="px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider mt-0.5 whitespace-normal text-left leading-snug max-w-[170px] inline-block">{c.timeBarRisk}</span>}
              </td>
              <td className="px-2 py-1.5 text-foreground/80">
                {c.estimatedValue}
                {c.quantumBasis && <div className="text-muted-foreground text-[10px]">{c.quantumBasis}</div>}
              </td>
              <td className="px-2 py-1.5 text-center font-semibold">{c.successProbabilityPct != null ? `${c.successProbabilityPct}%` : '—'}</td>
              <td className="px-2 py-1.5"><span style={{ background: tone(c.strength).bg, color: tone(c.strength).color }} className="inline-block px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider">{c.strength}</span></td>
              <td className="px-2 py-1.5 text-foreground/80">
                {(c.nextSteps || []).slice(0, 2).map((s, j) => <div key={j}>• {s}</div>)}
                {c.counterArguments && <div className="text-muted-foreground text-[10px] mt-0.5">Counter: {c.counterArguments}</div>}
              </td>
              <td className="px-2 py-1.5">
                <div className="flex items-center gap-1">
                  {onShowSource && (c.documentReference || c.quotedText) && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-purple-500"
                            onClick={() => onShowSource({ id: c.id, title: c.title, documentReference: c.documentReference, clauseReference: c.clauseReference, quotedText: c.quotedText })}
                            data-testid={`claim-opp-source-${c.id || i}`}>
                      <MapPin className="w-3 h-3 mr-1" />Source
                    </Button>
                  )}
                  <ExplainButton kind="claimOpportunity" componentKey={c.id || `CO${i + 1}`}
                                 label="Claim Opportunity" component={c} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LdAnalysisPanel({ ld }) {
  if (!ld) return null;
  const tiles = [
    ['LD rate / day', ld.ldRatePerDay], ['Cap position', ld.capPosition],
    ['Days at risk', ld.daysAtRisk], ['Current exposure', ld.currentExposure],
    ['Exposure vs cap', ld.exposureVsCapPct], ['Concurrency', ld.concurrencyPosition],
  ].filter(([, v]) => v);
  return (
    <div className="space-y-2 text-xs" data-testid="ld-analysis-panel">
      <div className="flex justify-end -mt-1">
        <ExplainButton kind="ldAnalysis" componentKey="LD" label="Liquidated Damages Analysis" component={ld} />
      </div>
      {ld.regimeSummary && <p className="text-foreground/80 leading-relaxed">{ld.regimeSummary}</p>}
      {tiles.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {tiles.map(([k, v]) => (
            <div key={k} className="rounded p-2" style={{ border: '1px solid var(--border)' }}>
              <div className="text-[10px] uppercase text-muted-foreground/70">{k}</div>
              <div className="text-foreground">{v}</div>
            </div>
          ))}
        </div>
      )}
      {ld.delayEventsFound?.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-muted-foreground uppercase text-[10px]"><tr>{['Delay event', 'Days', 'Attributable', 'Source', 'Defence'].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
          <tbody>{ld.delayEventsFound.map((e, i) => (
            <tr key={i} className="border-t border-border align-top">
              <td className="px-2 py-1.5 text-foreground">{e.event}</td>
              <td className="px-2 py-1.5">{e.days}</td>
              <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{e.attributable}</Badge></td>
              <td className="px-2 py-1.5 text-[10px] text-blue-400 truncate max-w-[140px]" title={e.documentReference}>{e.documentReference}</td>
              <td className="px-2 py-1.5 text-foreground/80">{e.defence}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {ld.defences?.length > 0 && <ul className="list-disc list-inside text-foreground/80">{ld.defences.map((d, i) => <li key={i}>{d}</li>)}</ul>}
      {ld.recommendation && <p className="text-foreground"><span className="text-muted-foreground/70">Recommendation: </span>{ld.recommendation}</p>}
    </div>
  );
}

export function VariationsAnalysisPanel({ va }) {
  if (!va) return null;
  return (
    <div className="space-y-2 text-xs" data-testid="variations-analysis-panel">
      {va.summary && <p className="text-foreground/80 leading-relaxed">{va.summary}</p>}
      {va.items?.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-muted-foreground uppercase text-[10px]"><tr>{['Variation', 'Instruction', 'Value', 'Valuation basis', 'Status', 'Recommendation', ''].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
          <tbody>{va.items.map((v, i) => (
            <tr key={i} className="border-t border-border align-top">
              <td className="px-2 py-1.5 text-foreground">{v.title}
                {v.documentReference && <div className="text-[10px] text-blue-400 truncate max-w-[150px]" title={v.documentReference}>{v.documentReference}</div>}</td>
              <td className="px-2 py-1.5">{v.instructionReference || '—'}</td>
              <td className="px-2 py-1.5 text-foreground/80">{v.valueClaimed}</td>
              <td className="px-2 py-1.5 text-foreground/80">{v.valuationBasis}</td>
              <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{v.status}</Badge></td>
              <td className="px-2 py-1.5 text-foreground/80">{v.recommendation}</td>
              <td className="px-2 py-1.5">
                <ExplainButton kind="variation" componentKey={`VA${i + 1}`} label="Variation" component={v} compact />
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {va.unpricedOrUninstructedWork?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase text-muted-foreground/70 mb-1">Unpriced / uninstructed work spotted</div>
          <ul className="list-disc list-inside text-foreground/80">{va.unpricedOrUninstructedWork.map((u, i) => <li key={i}>{u}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

export function DisputesAnalysisPanel({ da }) {
  if (!da) return null;
  return (
    <div className="space-y-2 text-xs" data-testid="disputes-analysis-panel">
      {da.summary && <p className="text-foreground/80 leading-relaxed">{da.summary}</p>}
      {da.items?.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-muted-foreground uppercase text-[10px]"><tr>{['Dispute', 'Stage', 'Our position', 'Exposure', 'Strength', 'Strategy', ''].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
          <tbody>{da.items.map((d, i) => (
            <tr key={i} className="border-t border-border align-top">
              <td className="px-2 py-1.5 text-foreground">{d.title}
                {d.documentReference && <div className="text-[10px] text-blue-400 truncate max-w-[150px]" title={d.documentReference}>{d.documentReference}</div>}</td>
              <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{d.stage}</Badge></td>
              <td className="px-2 py-1.5 text-foreground/80">{d.position}</td>
              <td className="px-2 py-1.5 text-foreground/80">{d.exposure}</td>
              <td className="px-2 py-1.5"><Badge style={{ background: tone(d.strength).bg, color: tone(d.strength).color }} className="text-[9px]">{d.strength}</Badge></td>
              <td className="px-2 py-1.5 text-foreground/80">{d.strategy}</td>
              <td className="px-2 py-1.5">
                <ExplainButton kind="dispute" componentKey={`DP${i + 1}`} label="Dispute" component={d} compact />
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {da.escalationPathway && <p className="text-foreground/80"><span className="text-muted-foreground/70">Escalation pathway: </span>{da.escalationPathway}</p>}
      {da.limitationOrTimeBarNotes && <p className="text-amber-400">{da.limitationOrTimeBarNotes}</p>}
    </div>
  );
}
